import React, { useEffect, useState, useCallback } from "react";
import BrandLayout from "@/components/layout/BrandLayout";
import { useAuth, API } from "@/App";
import axios from "axios";
import { BarChart3, Music, FileText, Users, RefreshCw } from "lucide-react";

const BrandAnalytics = () => {
  const { token } = useAuth();
  const [analytics, setAnalytics] = useState({
    total_impressions: 0,
    total_track_plays: 0,
    total_submissions: 0,
    artists_participated: 0
  });
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/campaign-analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAnalytics();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchAnalytics, 30000);
    
    return () => clearInterval(interval);
  }, [fetchAnalytics]);

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toLocaleString();
  };

  const StatCard = ({ icon: Icon, label, value, color, bgColor }) => (
    <div className={`${bgColor} border border-gray-700 p-8 flex flex-col items-center justify-center text-center transition-transform hover:scale-105`}>
      <div className={`w-16 h-16 ${color} bg-black/50 rounded-full flex items-center justify-center mb-4 shadow-lg`}>
        <Icon size={32} />
      </div>
      <p className="text-5xl font-heading text-white mb-2">{formatNumber(value)}</p>
      <p className="text-sm text-gray-400 uppercase tracking-wider">{label}</p>
    </div>
  );

  return (
    <BrandLayout>
      <div className="animate-fade-in" data-testid="brand-analytics">
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-4xl font-heading text-white mb-2">CAMPAIGN ANALYTICS</h1>
            <p className="text-gray-400">Real-time campaign performance metrics</p>
          </div>
          <div className="flex items-center gap-4">
            <button 
              onClick={fetchAnalytics}
              className="flex items-center gap-2 text-gray-400 hover:text-[#F58220] transition-colors"
              title="Refresh data"
            >
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
              <span className="text-sm">Refresh</span>
            </button>
            {lastUpdated && (
              <span className="text-xs text-gray-500">
                Updated: {lastUpdated.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>

        {/* Auto-refresh indicator */}
        <div className="mb-6 flex items-center gap-2 text-sm text-gray-500">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span>Auto-refreshing every 30 seconds</span>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-gray-800 animate-pulse h-48 rounded"></div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              icon={BarChart3}
              label="Total Impressions"
              value={analytics.total_impressions}
              color="text-purple-400"
              bgColor="bg-gradient-to-br from-purple-900/40 to-pink-900/40"
            />
            <StatCard
              icon={Music}
              label="Total Track Plays"
              value={analytics.total_track_plays}
              color="text-blue-400"
              bgColor="bg-gradient-to-br from-blue-900/40 to-cyan-900/40"
            />
            <StatCard
              icon={FileText}
              label="Total Submissions"
              value={analytics.total_submissions}
              color="text-orange-400"
              bgColor="bg-gradient-to-br from-orange-900/40 to-yellow-900/40"
            />
            <StatCard
              icon={Users}
              label="Artists Participated"
              value={analytics.artists_participated}
              color="text-green-400"
              bgColor="bg-gradient-to-br from-green-900/40 to-emerald-900/40"
            />
          </div>
        )}

        {/* Summary Card */}
        <div className="mt-8 bg-gray-900 border border-gray-700 p-6">
          <h2 className="font-heading text-xl text-white mb-4">CAMPAIGN SUMMARY</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex justify-between py-2 border-b border-gray-800">
              <span className="text-gray-400">Average Impressions per Submission</span>
              <span className="font-semibold text-white">
                {analytics.total_submissions > 0 
                  ? formatNumber(Math.round(analytics.total_impressions / analytics.total_submissions))
                  : '0'}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-800">
              <span className="text-gray-400">Average Track Plays per Submission</span>
              <span className="font-semibold text-white">
                {analytics.total_submissions > 0 
                  ? (analytics.total_track_plays / analytics.total_submissions).toFixed(1)
                  : '0'}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-800">
              <span className="text-gray-400">Average Submissions per Artist</span>
              <span className="font-semibold text-white">
                {analytics.artists_participated > 0 
                  ? (analytics.total_submissions / analytics.artists_participated).toFixed(1)
                  : '0'}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-800">
              <span className="text-gray-400">Average Impressions per Artist</span>
              <span className="font-semibold text-white">
                {analytics.artists_participated > 0 
                  ? formatNumber(Math.round(analytics.total_impressions / analytics.artists_participated))
                  : '0'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </BrandLayout>
  );
};

export default BrandAnalytics;
